import EventEmitter from 'eventemitter3';
import { isNaN } from 'lodash-es';
import { clamp } from 'utils/math';
import getDuration from './getDuration';
import { AudioPlayer } from './types';

export default class StubAudioPlayer implements AudioPlayer {
  currentTime = 0;

  duration = NaN;

  paused = true;

  sampleRate = 1;

  isDisabled: boolean;

  lastSeekReason = 'programmatic' as const;

  private emitter: EventEmitter;

  constructor(public src: string, disabled = false) {
    this.emitter = new EventEmitter();
    this.isDisabled = disabled;
    this.initialize();
  }

  private async initialize() {
    this.emitter.emit('loadstart');
    this.duration = await getDuration(this.src);
    this.emitter.emit('durationchange');
    this.emitter.emit('loadedmetadata');
    this.emitter.emit('canplay');
  }

  pause(): void {
    this.paused = true;
    this.emitter.emit('pause');
  }

  play(): Promise<void> | undefined {
    this.paused = false;
    this.emitter.emit('play');
    this.emitter.emit('playing');
    return Promise.resolve();
  }

  seek(seconds: number): void {
    this.emitter.emit('seeking');
    this.currentTime = clamp(seconds, 0, this.duration);
    this.emitter.emit('seeked');
  }

  public async togglePlay() {
    if (this.paused) {
      return this.play();
    } else {
      this.pause();
    }
  }

  enablePlayer(): void {
    this.isDisabled = false;
  }

  disablePlayer(): void {
    this.isDisabled = true;
  }

  off(type: 'timeupdate', listener: (currentTime: number) => void): void;
  off<T extends keyof HTMLMediaElementEventMap>(
    type: T,
    listener: (this: HTMLMediaElement, ev: HTMLMediaElementEventMap[T]) => void,
    opts?: boolean | AddEventListenerOptions | undefined,
  ): void;
  off(type: string, listener: unknown, opts?: unknown): void {
    this.emitter.off(
      type,
      listener as EventEmitter.EventListener<string, string>,
    );
  }

  on(type: 'timeupdate', listener: (currentTime: number) => void): void;
  on<T extends keyof HTMLMediaElementEventMap>(
    type: T,
    listener: (this: HTMLMediaElement, ev: HTMLMediaElementEventMap[T]) => void,
    opts?: boolean | AddEventListenerOptions | undefined,
  ): void;
  on(type: string, listener: unknown, opts?: unknown): void {
    this.emitter.on(
      type,
      listener as EventEmitter.EventListener<string, string>,
    );
  }

  public get ready() {
    return !isNaN(this.duration);
  }
}
